import React, { useMemo, useState } from 'react'
import gql from 'graphql-tag'
import { useQuery } from '@apollo/client'
import Skeleton from 'react-loading-skeleton'
import moment from 'moment-timezone'
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap'
import HistoryTable, { HistoryColumnPicker, useHistoryColumns } from './HistoryTable'
import './HistoryList.css'

const BQHistoryQuery = gql`
    query BQHistoryQuery($objectIds: [ID!]!, $startDate: Date, $endDate: Date, $fields: [String!]) {
        bqHistory(objectIds: $objectIds, startDate: $startDate, endDate: $endDate, fields: $fields) {
            items {
                appLabel
                modelName
                objectId
                eventTimestamp
                actorName
                actorRole
                isNew
                isDeleted
                snapshot
                dirtyFields
                comment
                requestId
                source
            }
        }
    }
`

const ISO = moment.HTML5_FMT.DATE

// One list, so choosing a window is one click and every option is visible at
// once: the recent windows people usually want, then ever longer ones for old
// records with no recent activity. Every window ends today. The longest must stay
// within bqHistory's cap (MAX_BQ_OBJECT_HISTORY_RANGE_YEARS in activity/history.py).
const RANGE_OPTIONS = [
    { value: 'days:7', label: 'Last 7 days' },
    { value: 'days:30', label: 'Last 30 days' },
    { value: 'days:90', label: 'Last 90 days' },
    { value: 'months:6', label: 'Last 6 months' },
    { value: 'months:12', label: 'Last 12 months' },
    { value: 'months:18', label: 'Last 18 months' },
    { value: 'months:24', label: 'Last 2 years' },
    { value: 'months:36', label: 'Last 3 years' },
]
const DEFAULT_RANGE_VALUE = 'days:7'

// Counted so today is one of the N days / months: "last 7 days" starts 6 days ago.
const rangeFromValue = (value) => {
    const [unit, amount] = String(value).split(':')
    return {
        start: moment().subtract(Number(amount), unit).add(1, 'day').format(ISO),
        end: moment().format(ISO),
    }
}

// `defaultRange` is the window the log opens on, one of RANGE_OPTIONS' values (e.g. 'days:30').
function BQHistoryList({ objectIds = [], fields = null, defaultRange = DEFAULT_RANGE_VALUE, emptyText = 'No history found yet.', subject = null }) {
    const initialRangeValue = RANGE_OPTIONS.some((option) => option.value === defaultRange)
        ? defaultRange
        : DEFAULT_RANGE_VALUE
    // The chosen option *is* the range -- every option yields a valid one, so
    // there is nothing to validate, no draft, and no Apply.
    const [rangeValue, setRangeValue] = useState(initialRangeValue)
    const [isRangeMenuOpen, setIsRangeMenuOpen] = useState(false)
    // Held here rather than in the table, so the picker can sit beside the range and a
    // new range (which remounts the table) keeps the view.
    const [columnConfig, setColumnConfig] = useHistoryColumns()
    const range = useMemo(() => rangeFromValue(rangeValue), [rangeValue])
    const selectedRangeLabel = RANGE_OPTIONS.find((option) => option.value === rangeValue)?.label
    const normalizedObjectIds = useMemo(
        () => (objectIds || []).filter(Boolean),
        [objectIds]
    )
    // Null asks for every change; a list, only the rows that changed one of those fields.
    const normalizedFields = useMemo(() => {
        const cleaned = (fields || []).filter(Boolean)
        return cleaned.length ? cleaned : null
    }, [fields])
    const isDefaultDateRange = rangeValue === initialRangeValue

    const { data, loading, error } = useQuery(BQHistoryQuery, {
        variables: {
            objectIds: normalizedObjectIds,
            startDate: range.start,
            endDate: range.end,
            fields: normalizedFields,
        },
        skip: !normalizedObjectIds.length,
        fetchPolicy: 'cache-and-network',
    })

    const header = (
        <div className="vgm-audit-header">
            <div className="vgm-audit-header-titles">
                <h2 className="vgm-audit-title">Activity Logs</h2>
                <p className="vgm-audit-subtitle">
                    {subject ? `Every recorded change to ${subject}` : 'Every recorded change'}, who made it and when.
                </p>
            </div>
            <div className="vgm-audit-header-controls">
                {/* The same link as PREFERENCE beside it, sized to the chosen window's label.
                    A menu rather than a native select: the browser opens a select's list over
                    the control itself, and this list belongs under it. */}
                <Dropdown
                    isOpen={isRangeMenuOpen}
                    toggle={() => setIsRangeMenuOpen((open) => !open)}
                    disabled={loading}
                    className={`vgm-audit-daterange${loading ? ' is-disabled' : ''}`}
                >
                    <DropdownToggle
                        tag="a"
                        id="bq-history-range"
                        tabIndex={0}
                        className="driver-list-button vgm-audit-range-toggle"
                        aria-label={`Showing ${selectedRangeLabel}`}
                    >
                        {selectedRangeLabel} | <i className="fa fa-clock-o" aria-hidden="true"></i>
                    </DropdownToggle>
                    <DropdownMenu end className="vgm-audit-range-menu">
                        {RANGE_OPTIONS.map((option) => (
                            <DropdownItem
                                key={option.value}
                                active={option.value === rangeValue}
                                onClick={() => setRangeValue(option.value)}
                            >
                                {option.label}
                            </DropdownItem>
                        ))}
                    </DropdownMenu>
                </Dropdown>
                <HistoryColumnPicker columnConfig={columnConfig} setColumnConfig={setColumnConfig} placement="bottom" />
            </div>
        </div>
    )

    const historyItems = data?.bqHistory?.items || []

    if (loading && !historyItems.length) {
        return (
            <div className="vgm-audit-trail">
                {header}
                <Skeleton height={90} count={4} className="mb-2" />
            </div>
        )
    }

    if (error) {
        return (
            <div className="vgm-audit-trail">
                {header}
                <div className="alert alert-danger mb-0">{error.message}</div>
            </div>
        )
    }

    if (!historyItems.length) {
        return (
            <div className="vgm-audit-trail">
                {header}
                <p className="vgm-audit-empty">
                    {isDefaultDateRange
                        ? `No history found in the ${selectedRangeLabel.toLowerCase()}. Try expanding the date range.`
                        : emptyText}
                </p>
            </div>
        )
    }

    return (
        <div className="vgm-audit-trail">
            {header}
            <HistoryTable
                key={`${normalizedObjectIds.join('|')}|${range.start}|${range.end}`}
                items={historyItems}
                columnConfig={columnConfig}
                fields={normalizedFields}
            />
        </div>
    )
}

export default BQHistoryList
